import { type ReactElement, type ReactNode } from 'react';
import { AddDialog } from '@/components/AddDialog';
import { fontFamilies, r, s } from '@/theme/tokens';
import { Text } from '@/ui';
import { Box, type DialogProps } from '@/ui/primitives';
import { getDetailEditEntityDialogIcon } from './detailEditEntityDialogIcon';

const entityNameCodeSx = {
  display: 'inline',
  fontFamily: fontFamilies.mono,
  backgroundColor: 'action.hover',
  borderRadius: s('xxs'),
  pl: s('sm'),
  pr: s('xs'),
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  boxDecorationBreak: 'clone',
} as const;

export const editDialogInputBackgroundColor = 'background.default' as const;

type EntityEditDialogProps = {
  open: boolean;
  entityLabel: string;
  entityName: string;
  entityRunNumber?: number | null;
  saving?: boolean;
  disableSave?: boolean;
  saveLabel?: string;
  errorMessage?: string | null;
  maxWidth?: DialogProps['maxWidth'];
  onClose: () => void;
  onSave: () => void | Promise<void>;
  children: ReactNode;
};

export const EntityEditDialog = ({
  open,
  entityLabel,
  entityName,
  entityRunNumber,
  saving = false,
  disableSave = false,
  saveLabel = 'Save',
  errorMessage,
  maxWidth = 'sm',
  onClose,
  onSave,
  children,
}: EntityEditDialogProps): ReactElement => {
  const resolvedEntityName = entityName.trim() || `this ${entityLabel.toLowerCase()}`;
  const resolvedEntityRunLabel =
    entityRunNumber !== null && entityRunNumber !== undefined
      ? `${resolvedEntityName} #${String(entityRunNumber)}`
      : resolvedEntityName;

  return (
    <AddDialog
      open={open}
      title={`Edit ${entityLabel}`}
      titleIcon={getDetailEditEntityDialogIcon(entityLabel)}
      maxWidth={maxWidth}
      saving={saving}
      disableSave={disableSave}
      saveLabel={saveLabel}
      dialogRadius={r('md')}
      childrenGap="xl"
      footerGap="xl"
      description={
        <Text role="body" color="textSecondary">
          Update details for{' '}
          <Box component="code" sx={entityNameCodeSx}>
            {resolvedEntityRunLabel}
          </Box>
          .
        </Text>
      }
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      {children}
    </AddDialog>
  );
};
